import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from '../api/scenariosApi';
import type {
  CreateScenarioRequest,
  SaveScenarioContentRequest,
  UpdateScenarioRequest,
  UpdateSchedulingRequest,
} from '../types';

const PAGE_SIZE = 30;

export const scenarioKeys = {
  all: ['scenarios'] as const,
  lists: () => [...scenarioKeys.all, 'list'] as const,
  list: (search: string) => [...scenarioKeys.lists(), search] as const,
  details: () => [...scenarioKeys.all, 'detail'] as const,
  detail: (id: string) => [...scenarioKeys.details(), id] as const,
};

export function useScenariosInfinite(search: string) {
  return useInfiniteQuery({
    queryKey: scenarioKeys.list(search),
    queryFn: ({ pageParam }) =>
      api.listScenarios({ search: search || undefined, page: pageParam, pageSize: PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.totalPages ? last.page + 1 : undefined),
  });
}

export function useScenario(id: string | undefined) {
  return useQuery({
    queryKey: scenarioKeys.detail(id ?? ''),
    queryFn: () => api.getScenario(id!),
    enabled: !!id,
  });
}

export function useCreateScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateScenarioRequest) => api.createScenario(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: scenarioKeys.lists() }),
  });
}

export function useUpdateScenario(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateScenarioRequest) => api.updateScenario(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: scenarioKeys.detail(id) });
      qc.invalidateQueries({ queryKey: scenarioKeys.lists() });
    },
  });
}

export function useDeleteScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteScenario(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: scenarioKeys.lists() }),
  });
}

export function useSetScenarioActive(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (isActive: boolean) => api.setScenarioActive(id, isActive),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: scenarioKeys.detail(id) });
      qc.invalidateQueries({ queryKey: scenarioKeys.lists() });
    },
  });
}

export function useSetScenarioLocations(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (locationIds: string[]) => api.setScenarioLocations(id, locationIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: scenarioKeys.detail(id) }),
  });
}

export function useUpdateScheduling(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateSchedulingRequest) => api.updateScenarioScheduling(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: scenarioKeys.detail(id) });
      qc.invalidateQueries({ queryKey: scenarioKeys.lists() });
    },
  });
}

export function useSaveScenarioContent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SaveScenarioContentRequest) => api.saveScenarioContent(id, data),
    onSuccess: (detail) => {
      qc.setQueryData(scenarioKeys.detail(id), detail);
      qc.invalidateQueries({ queryKey: scenarioKeys.lists() });
    },
  });
}
